import { Prisma, type Job, type PrismaClient } from '@prisma/client';
import type { Config } from './config.js';
import type { Logger } from './logger.js';

/**
 * DB-backed job queue (PLAN.md §4). Jobs survive restarts, are claimed atomically (so two runners
 * or two ticks never run the same job twice) and are idempotent through `idempotencyKey`.
 */
export interface JobContext {
  db: PrismaClient;
  config: Config;
  logger: Logger;
  now: Date;
}
/** A handler may ask to run again later (e.g. outside the send window) without counting as a failure. */
export type JobHandler = (job: Job, ctx: JobContext) => Promise<void | { rescheduleAt: Date }>;

const handlers = new Map<string, JobHandler>();
export const registerJobHandler = (type: string, handler: JobHandler) =>
  handlers.set(type, handler);

export interface EnqueueInput {
  companyId: number;
  type: string;
  payload?: Prisma.InputJsonValue;
  runAt?: Date;
  idempotencyKey?: string;
  maxAttempts?: number;
}

type Db = PrismaClient | Prisma.TransactionClient;

/** Returns the existing job instead of creating a duplicate when the key was used before. */
export async function enqueueJob(db: Db, input: EnqueueInput): Promise<Job> {
  try {
    return await db.job.create({
      data: {
        companyId: input.companyId,
        type: input.type,
        payload: input.payload ?? Prisma.JsonNull,
        runAt: input.runAt ?? new Date(),
        idempotencyKey: input.idempotencyKey,
        maxAttempts: input.maxAttempts ?? 5,
      },
    });
  } catch (e) {
    if (
      input.idempotencyKey &&
      e instanceof Prisma.PrismaClientKnownRequestError &&
      e.code === 'P2002'
    ) {
      const existing = await db.job.findUnique({ where: { idempotencyKey: input.idempotencyKey } });
      if (existing) return existing;
    }
    throw e;
  }
}

/** Cancels pending jobs whose key starts with `prefix` (e.g. when a document changes status). */
export async function cancelJobs(db: Db, keyPrefix: string): Promise<number> {
  const res = await db.job.updateMany({
    where: { idempotencyKey: { startsWith: keyPrefix }, status: 'pending' },
    data: { status: 'cancelled' },
  });
  return res.count;
}

const STALE_LOCK_MS = 5 * 60_000;
export const backoffMs = (attempt: number) => Math.min(30_000 * 2 ** (attempt - 1), 3_600_000);

/** Runs every due job once. Returns how many were executed. Safe to call from cron. */
export async function runDueJobs(
  ctxBase: Omit<JobContext, 'now'>,
  now: Date = new Date(),
  limit = 25,
): Promise<number> {
  const { db, logger } = ctxBase;
  const ctx: JobContext = { ...ctxBase, now };

  // A runner that crashed mid-job leaves it "running"; give it back after a timeout.
  await db.job.updateMany({
    where: { status: 'running', lockedAt: { lt: new Date(now.getTime() - STALE_LOCK_MS) } },
    data: { status: 'pending', lockedAt: null },
  });

  const due = await db.job.findMany({
    where: { status: 'pending', runAt: { lte: now } },
    orderBy: [{ runAt: 'asc' }, { id: 'asc' }],
    take: limit,
  });

  let executed = 0;
  for (const candidate of due) {
    const claimed = await db.job.updateMany({
      where: { id: candidate.id, status: 'pending' },
      data: { status: 'running', lockedAt: now, attempts: { increment: 1 } },
    });
    if (claimed.count !== 1) continue; // another runner took it

    const job = { ...candidate, attempts: candidate.attempts + 1 };
    const handler = handlers.get(job.type);
    try {
      if (!handler) throw new Error(`No handler registered for job type "${job.type}"`);
      const result = await handler(job, ctx);
      if (result && 'rescheduleAt' in result) {
        // Not a failure and not an attempt: try again at the requested time.
        await db.job.update({
          where: { id: job.id },
          data: {
            status: 'pending',
            lockedAt: null,
            runAt: result.rescheduleAt,
            attempts: { decrement: 1 },
          },
        });
      } else {
        await db.job.update({
          where: { id: job.id },
          data: { status: 'done', lockedAt: null, lastError: null },
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.warn(
        { jobId: job.id, type: job.type, attempt: job.attempts },
        `Job failed: ${message}`,
      );
      const exhausted = job.attempts >= job.maxAttempts;
      await db.job.update({
        where: { id: job.id },
        data: {
          status: exhausted ? 'failed' : 'pending',
          lockedAt: null,
          lastError: message.slice(0, 2000),
          runAt: exhausted ? job.runAt : new Date(now.getTime() + backoffMs(job.attempts)),
        },
      });
    }
    executed += 1;
  }
  return executed;
}

export class JobRunner {
  private timer: NodeJS.Timeout | undefined;
  private busy = false;
  private lastSweep = 0;

  constructor(
    private readonly ctx: Omit<JobContext, 'now'>,
    private readonly intervalMs = 5000,
    /** Housekeeping run at most every 30 minutes (e.g. expiring old quotations). */
    private readonly sweep?: () => Promise<unknown>,
  ) {}

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => void this.tick(), this.intervalMs);
    this.timer.unref();
  }

  async tick() {
    if (this.busy) return;
    this.busy = true;
    try {
      await runDueJobs(this.ctx);
      if (this.sweep && Date.now() - this.lastSweep > 30 * 60_000) {
        this.lastSweep = Date.now();
        await this.sweep();
      }
    } catch (err) {
      this.ctx.logger.error({ err }, 'Job runner tick failed');
    } finally {
      this.busy = false;
    }
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }
}
